import { z } from "zod";
import { DbCreateInputOmit, DbUpdate, nullishToOptional } from "../../helpers";
import { OmitStrict } from "../../utils";
import { ImportWarning } from "../import/warning";
import { SourceImportStorybookSummary, SourceImportSummary } from "./import-summary";

//
// Enums
//

export const DataSourceRemoteType = z.enum(["Figma", "TokenStudio", "FigmaVariablesPlugin", "Storybook"]);
export const DataSourceUploadRemoteSource = z.enum(["TokenStudio", "FigmaVariablesPlugin", "Custom"]);
export const DataSourceFigmaState = z.enum(["Active", "MissingIntegration", "MissingFileAccess", "MissingFileOwner"]);
export const DataSourceAutoImportMode = z.enum(["Never", "Hourly"]);

export type DataSourceRemoteType = z.infer<typeof DataSourceRemoteType>;
export type DataSourceUploadRemoteSource = z.infer<typeof DataSourceUploadRemoteSource>;
export type DataSourceFigmaState = z.infer<typeof DataSourceFigmaState>;
export type DataSourceAutoImportMode = z.infer<typeof DataSourceAutoImportMode>;

//
// Aux
//

export const DataSourceStats = z.object({
  tokens: zeroNumberByDefault(),
  components: zeroNumberByDefault(),
  assets: zeroNumberByDefault(),
  frames: zeroNumberByDefault(),
});

export type DataSourceStats = z.infer<typeof DataSourceStats>;

//
// Figma
//

export const DataSourceFigmaFileData = z.object({
  lastUpdatedAt: z.coerce.date(),
});

export const DataSourceFigmaFileVersionData = z.object({
  id: z.string(),
  label: z.string().optional(),
  description: z.string().optional(),
  createdAt: z.coerce.date(),
});

export const DataSourceFigmaScope = z.object({
  assets: z.boolean(),
  components: z.boolean(),
  documentationFrames: z.boolean(),
  tokens: z.boolean(),
  themePersistentId: z.string().optional(),
  isUnpublishedContentFallbackEnabled: z.boolean(),
});

export const DataSourceFigmaImportMetadata = z.object({
  fileData: DataSourceFigmaFileData.optional(),
  importedPublishedVersion: DataSourceFigmaFileVersionData.optional(),
});

export const DataSourceFigmaRemote = z.object({
  type: z.literal(DataSourceRemoteType.Enum.Figma),
  fileId: z.string(),
  preferredCredentialId: z.string().optional(),
  ownerId: z.string(), // todo remove or keep to reference who created data source
  ownerName: z.string(), // todo probably remove
  scope: DataSourceFigmaScope,
  state: DataSourceFigmaState,
  requiresSync: z
    .boolean()
    .optional()
    .transform(v => v ?? false),
  lastImportMetadata: DataSourceFigmaImportMetadata.optional(),
  downloadChunkSize: z.number().optional(),
  figmaRenderChunkSize: z.number().optional(),
  maxFileDepth: z.number().optional(),
});

export type DataSourceFigmaFileData = z.infer<typeof DataSourceFigmaFileData>;
export type DataSourceFigmaFileVersionData = z.infer<typeof DataSourceFigmaFileVersionData>;
export type DataSourceFigmaScope = z.infer<typeof DataSourceFigmaScope>;
export type DataSourceFigmaImportMetadata = z.infer<typeof DataSourceFigmaImportMetadata>;
export type DataSourceFigmaRemote = z.infer<typeof DataSourceFigmaRemote>;

//
// Token Studio
//

export const DataSourceTokenStudioRemote = z.object({
  type: z.literal(DataSourceRemoteType.Enum.TokenStudio),
});

export type DataSourceTokenStudioRemote = z.infer<typeof DataSourceTokenStudioRemote>;

//
// Figma Variables Plugin
//

export const DataSourceUploadImportMetadata = z.record(z.any());

export const DataSourceUploadRemote = z.object({
  type: z.literal(DataSourceRemoteType.Enum.FigmaVariablesPlugin),
  remoteId: z.string(),
  remoteSourceType: DataSourceUploadRemoteSource,
  lastImportMetadata: DataSourceUploadImportMetadata.optional(),
  warnings: nullishToOptional(ImportWarning.array()),
  isTokenTypeSplitEnabled: z.boolean(),
  isCollectionsMigrationCompleted: z.boolean(),
});

export type DataSourceUploadImportMetadata = z.infer<typeof DataSourceUploadImportMetadata>;
export type DataSourceUploadRemote = z.infer<typeof DataSourceUploadRemote>;

//
// Storybook
//

export const DataSourceStorybookRemote = z.object({
  type: z.literal(DataSourceRemoteType.Enum.Storybook),
  indexUrl: z.string(),
  userUrl: z.string(),
  ownerId: z.string().optional(),
  lastImportedAt: z.coerce.date().optional(),
  lastImportedSuccessfully: z.coerce.date().optional(),
  lastImportedResult: SourceImportStorybookSummary.optional(),
  isFailed: z.boolean(),
  storiesCount: z.number(),
  docsCount: z.number(),
  description: z.string().optional(),
  isUsingVpn: z.boolean().optional(),
});

export type DataSourceStorybookRemote = z.infer<typeof DataSourceStorybookRemote>;

//
// Base
//

export const DataSourceRemote = z.discriminatedUnion("type", [
  DataSourceFigmaRemote,
  DataSourceUploadRemote,
  DataSourceTokenStudioRemote,
  DataSourceStorybookRemote,
]);

export const DataSource = z.object({
  id: z.string(),

  name: z.string(),
  thumbnailUrl: z.string().optional(),

  createdAt: z.coerce.date().optional(),

  lastImportedAt: z.coerce.date().optional(),
  lastImportSummary: SourceImportSummary.optional(),

  designSystemId: z.string(),
  brandPersistentId: z.string(),
  autoImportMode: DataSourceAutoImportMode,
  stats: DataSourceStats,

  remote: DataSourceRemote,

  sortOrder: z.number(),
});

export const DataSourceVersion = z.object({
  id: z.string(),
  createdAt: z.coerce.date(),
  label: z.string().nullish(),
  description: z.string().nullish(),
});

export type DataSourceVersion = z.infer<typeof DataSourceVersion>;

export type DataSourceRemote = z.infer<typeof DataSourceRemote>;
export type DataSource = z.infer<typeof DataSource>;
export type DataSourceOfType<T extends DataSourceRemoteType> = DataSource & {
  remote: Extract<DataSourceRemote, { type: T }>;
};

export function isDataSourceOfType<T extends DataSourceRemoteType>(
  dataSource: DataSource,
  type: T
): dataSource is DataSourceOfType<T> {
  return dataSource.remote.type === type;
}

export type CreateDataSource = OmitStrict<DbCreateInputOmit<DataSource>, "sortOrder">;
export type UpdateDataSource = OmitStrict<DbUpdate<DataSource>, "brandPersistentId" | "designSystemId">;

function zeroNumberByDefault() {
  return z
    .number()
    .nullish()
    .transform(v => v ?? 0);
}
